import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import VideoFeed from '../components/VideoFeed';
import { getCurrentUser } from '../../lib/auth';
import { FaEllipsisV, FaUser } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import SubscriptionModal from '../../components/SubscriptionModal';

const VideoReels: React.FC = () => {
  const navigate = useNavigate();
  const user = getCurrentUser();
  const [showMenu, setShowMenu] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const menuItems = [
    { label: 'Profile', path: `/profile/${user?.id}` },
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Wallet', path: '/wallet' },
    { label: 'Coupons', path: '/coupons' }
  ];

  useEffect(() => {
    checkAccess();
  }, [user]);

  const checkAccess = async () => {
    if (!user) {
      setHasAccess(false);
      setIsLoading(false);
      return;
    }

    try {
      // Check for valid subscription or trial
      const { data: subscription, error } = await supabase
        .from('ponocouponusage')
        .select('valid_until')
        .eq('user_id', user.id)
        .gte('valid_until', new Date().toISOString())
        .maybeSingle();

      setHasAccess(!!subscription);
      setIsLoading(false);

      if (!subscription) {
        setShowSubscriptionModal(true);
      }
    } catch (error) {
      console.error('Error checking access:', error);
      setHasAccess(false);
      setIsLoading(false);
    }
  };

  const handleSubscribe = () => {
    setShowSubscriptionModal(false);
    setHasAccess(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black relative">
      {/* Menu Button */}
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="fixed top-4 right-4 z-50 p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-75 transition-colors"
      >
        <FaEllipsisV className="w-5 h-5" />
      </button>

      {/* Menu Dropdown */}
      <AnimatePresence>
        {showMenu && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed top-16 right-4 z-50 bg-black bg-opacity-90 rounded-xl shadow-lg overflow-hidden"
          >
            {menuItems.map((item, index) => (
              <motion.button
                key={item.path}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => {
                  setShowMenu(false);
                  navigate(item.path);
                }}
                className="flex items-center w-full px-6 py-3 text-left text-white hover:bg-white hover:bg-opacity-10 transition-colors"
              >
                {item.icon && <item.icon className="w-4 h-4 mr-3" />}
                {item.label}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {hasAccess ? (
        <VideoFeed />
      ) : (
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="text-center text-white">
            <h2 className="text-2xl font-bold mb-4">Premium Content</h2>
            <p className="text-gray-400 mb-4">Subscribe to access all videos</p>
            <button
              onClick={() => setShowSubscriptionModal(true)}
              className="px-6 py-3 bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
            >
              Subscribe Now
            </button>
          </div>
        </div>
      )}

      {showSubscriptionModal && (
        <SubscriptionModal
          onClose={() => setShowSubscriptionModal(false)}
          onSubscribe={handleSubscribe}
        />
      )}
    </div>
  );
};

export default VideoReels;